import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import routerStore from '@app/src/common/store/RouterStore';
import SidebarStore from '@app/src/common/components/sidebar/SidebarStore';
import {
  EXTERNAL_MODULES,
  SIDEBAR_ROUTES
} from '@app/src/common/components/navigation/constants/navigationRoutes';
import {
  buildModuleEntries,
  IModuleEntry
} from '@app/src/common/components/navigation/moduleEntries';
import HomeIcon from '@app/src/common/components/navigation/assets/Home.svg';

import './ModuleSelector.scss';

const HOME_LABEL_ID = 'app.navigation.home';

const ModuleSelector: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        SidebarStore.closeModuleSelector();
      }
    };
    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!containerRef.current) {
        return;
      }
      if (containerRef.current.contains(target)) {
        return;
      }
      const trigger = document.querySelector('.app-sidebar__module-trigger');
      if (trigger && trigger.contains(target)) {
        return;
      }
      SidebarStore.closeModuleSelector();
    };
    const handleBlur = () => SidebarStore.closeModuleSelector();

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('blur', handleBlur);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  const moduleEntries = buildModuleEntries(
    routerStore.routes,
    SIDEBAR_ROUTES,
    EXTERNAL_MODULES
  );

  const currentPath = routerStore.location && routerStore.location.pathname;
  const isHomeActive = currentPath === '/' || !currentPath;

  const handleHomeClick = () => {
    SidebarStore.closeModuleSelector();
    routerStore.targetPath = null;
    window.location.href = '/#/';
  };

  const handleModuleClick = (entry: IModuleEntry) => {
    SidebarStore.closeModuleSelector();
    if (entry.path) {
      routerStore.navigate(entry.path);
      return;
    }
    entry.navigate?.();
  };

  return createPortal(
    <div className="module-selector" ref={containerRef} role="dialog">
      <div className="module-selector__caret" />
      <button
        type="button"
        onClick={handleHomeClick}
        className={classNames('module-selector__tile', {
          'module-selector__tile_active': isHomeActive
        })}
      >
        <span className="module-selector__tile-icon">
          <HomeIcon />
        </span>
        <span className="module-selector__tile-label">
          <FormattedMessage id={HOME_LABEL_ID} />
        </span>
      </button>
      {moduleEntries.map(entry => {
        const Icon = entry.Icon;
        // External modules leave the platform app, so they never render as active here.
        const isActive =
          !!currentPath && !!entry.path && currentPath.startsWith(entry.path);
        return (
          <button
            type="button"
            key={entry.key}
            onClick={() => handleModuleClick(entry)}
            className={classNames('module-selector__tile', {
              'module-selector__tile_active': isActive
            })}
          >
            <span className="module-selector__tile-icon">
              <Icon />
            </span>
            <span className="module-selector__tile-label">
              <FormattedMessage id={entry.labelId} />
            </span>
          </button>
        );
      })}
    </div>,
    document.body
  );
};

export default observer(ModuleSelector);
